import { UserRole } from '@/core/Types'
import { SubscriptionStatusTypes } from '@/extern/StripeTypes'
import { AppColors } from '@/model/AppColors'
import { FieldValue, Timestamp } from '@/extern/FirebaseTypes'
import { toStringMDYY } from '@/core/DateUtils'
import { Coupon, Subscription } from '@/core/ApiTypes'

export function getUserRoleTitle(role: UserRole): string {
    if (role === UserRole.ADMIN)
        return 'Admin'
    if (role === UserRole.COMPANY_OWNER)
        return 'Owner'

    return 'User'
}

export function getUserRoleDescription(role: UserRole): string {
    if (role === UserRole.ADMIN)
        return 'System-wide admin'
    if (role === UserRole.COMPANY_OWNER)
        return 'Company admin who can manager users/subscriptions'

    return 'Normal user'
}

export function getSubscriptionStatusColor(subscription: Subscription): string {
    const status = subscription?.data?.status
    if (status === SubscriptionStatusTypes.active ||
        status === SubscriptionStatusTypes.trialing)
        return AppColors.primary
    if (status === SubscriptionStatusTypes.incomplete ||
        status === SubscriptionStatusTypes.incomplete_expired)
        return AppColors.secondary
    if (status === SubscriptionStatusTypes.past_due ||
        status === SubscriptionStatusTypes.unpaid)
        return AppColors.error

    return AppColors.info
}

export function getSubscriptionStatusText(subscription: Subscription): string {
    const status = subscription?.data?.status
    if (status) {
        switch (status) {
            case SubscriptionStatusTypes.active:
                return 'ACTIVE';
            case SubscriptionStatusTypes.canceled:
                return 'CANCELED';
            case SubscriptionStatusTypes.incomplete:
                return 'INCOMPLETE';
            case SubscriptionStatusTypes.incomplete_expired:
                return 'INCOMPLETE EXPIRED';
            case SubscriptionStatusTypes.past_due:
                return 'PAST DUE';
            case SubscriptionStatusTypes.trialing:
                return 'TRIALING';
            case SubscriptionStatusTypes.unpaid:
                return 'UNPAID';
        }
    }

    return 'UNKNOWN'
}

/**
 * Returns true if the coupon is valid and still available for use
 */
export function isCouponAvailable(coupon: Coupon): boolean {
    if (coupon?.data?.isValid !== true)
        return false

    if (coupon.data.maxRedemptions)
        return coupon.data.timesRedeemed < coupon.data.maxRedemptions

    return true
}

export function getDateText(timestamp: Timestamp|FieldValue): string {
    const millis = (timestamp as Timestamp)?.toMillis()
    return getDateTextFromMillis(millis)
}

export function getDateTextFromMillis(millis: number|null|undefined): string {
    if (millis === null || millis === undefined)
        return '---'

    return toStringMDYY(new Date(millis))
}

/**
 * Gets the currency amount text where the amount is given as cents instead of dollars as from in Stripe
 * @param amount Amount in cents
 * @param currency Currency code. 'USD' assumed if null.
 */
export function getCurrencyText(amount: number|null, currency?: string|null): string {
    const amountValue = amount || 0
    const symbol = (!currency || currency.toLowerCase() === 'usd') ? '$' : currency
    return symbol + (amountValue / 100).toLocaleString(undefined, {
        minimumFractionDigits: 2
    })
}

/**
 * Creates a pseudo random string of the specified length with alpha-numeric characters. The first character
 * will always be a letter.
 * @param length Length of the string
 */
export function createPseudoRandomString(length: number): string {
    const alpha = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz';
    const alphaNum = alpha + '0123456789';
    let result = '';

    for ( let i = 0; i < length; i++ ) {
        const characters = (i === 0) ? alpha : alphaNum
        result += characters.charAt(Math.floor(Math.random() * characters.length));
    }

    return result;
}